import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { PosturePanel, OriginMap, ScanReceiptPanel, Overview } from './Overview';
import { API, useAuth, atLeast, ROLE_LABEL } from './auth';
import { HistoryPanel } from './History';
import { UsersPanel } from './Users';
import { AlertsPanel } from './Alerts';
import { PageHelp } from './Help';

import { VerificationPanel } from './Verification';
import { IntelligencePanel } from './Intelligence';

/**
 * Abre un PDF protegido. La sesión vive en memoria (no en cookies), así que un
 * enlace directo llega sin credenciales y la API responde "No autenticado":
 * se descarga con el token y se abre como archivo local del navegador.
 */
const openPdf = async (url: string) => {
  const tab = window.open('', '_blank');   // se abre ya, para que el bloqueador no la frene
  try {
    const r = await axios.get(url, { responseType: 'blob' });
    const blobUrl = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
    if (tab) tab.location.href = blobUrl; else window.location.href = blobUrl;
    setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
  } catch {
    tab?.close();
    alert('No se pudo abrir el PDF');
  }
};



interface Threat {
  id: string;
  domain: string;
  type: string;
  score: number;
  impersonationScore?: number;
  severity?: string;
  nextCheckAt?: string | null;
  lastCheckedAt?: string | null;
  status: string;
  detectedAt: string;
  emailSent: boolean;
  metadata: any;
  company: { name: string; domain: string };
  validationDecision?: string | null;
  validatedBy?: string | null;
  validatedAt?: string | null;
  validationCriteria?: any;
}

interface DomainScan {
  id: string;
  totalVariants: number;
  activeThreats: number;
  status: string;
  startedAt: string;
  completedAt: string | null;
}

interface ExecutiveProfile {
  id: string;
  platform: string;
  handle: string;
  url: string;
  status: string;
  discoverySource: string;
  displayName?: string;
  bio?: string;
  followers?: number;
  photoSimilarity?: number;
  nameSimilarity?: number;
  needsHumanReview: boolean;
  firstSeen: string;
  executive?: { id: string; name: string; position?: string; company?: { name: string } };
}

interface AuthorityReport {
  id: string;
  authority: string;
  country: string;
  subject: string;
  body: string;
  status: string;
  approvedBy?: string | null;
  createdAt: string;
  threat?: { domain: string; score: number; severity?: string; company?: { name: string } };
}

interface Executive {
  id: string;
  name: string;
  email: string;
  position?: string;
  companyId: string;
  monitored?: boolean;
  consentRef?: string | null;
  photoPhash?: string | null;
  company?: { name: string };
}

interface Company {
  id: string;
  name: string;
  domain: string;
  industry: string;
  active: boolean;
  contactEmail: string;
  adminEmail: string;
  alertEmails: string[];
  scanEnabled: boolean;
  scanFrequency: number;
  lastScannedAt: string | null;
  threats: Threat[];
  executives: any[];
  domainScans: DomainScan[];
}

// La severidad nunca se comunica solo por color: color + icono + texto.
// Alrededor del 8% de los hombres tiene algún grado de daltonismo, y los
// comités de seguridad son mayoritariamente masculinos.
const SEVERITY_META: Record<string, { bar: string; text: string; icon: string }> = {
  CRITICAL: { bar: 'bg-red-500',    text: 'text-red-400',    icon: '' },
  HIGH:     { bar: 'bg-orange-500', text: 'text-orange-400', icon: '▲' },
  MEDIUM:   { bar: 'bg-yellow-500', text: 'text-yellow-400', icon: '◆' },
  LOW:      { bar: 'bg-blue-500',   text: 'text-blue-400',   icon: '●' },
  SAFE:     { bar: 'bg-green-500',  text: 'text-green-400',  icon: '✓' }
};

function severityOf(score: number, severity?: string): string {
  if (severity && SEVERITY_META[severity]) return severity;
  if (score >= 80) return 'CRITICAL';
  if (score >= 60) return 'HIGH';
  if (score >= 40) return 'MEDIUM';
  if (score >= 20) return 'LOW';
  return 'SAFE';
}

/** El color hace el trabajo que el número no hace: barra + etiqueta + icono. */
function ScoreBadge({ score, severity }: { score: number; severity?: string }) {
  const level = severityOf(score, severity);
  const meta = SEVERITY_META[level];
  return (
    <div className="min-w-[104px]">
      <div className="flex items-center gap-1.5">
        <span className={`text-xs ${meta.text}`}>{meta.icon}</span>
        <span className={`text-xs font-bold ${meta.text}`}>{level}</span>
        <span className="text-xs text-gray-500 ml-auto tabular-nums">{score}</span>
      </div>
      <div className="mt-1 h-1.5 w-full bg-gray-800 rounded-full overflow-hidden">
        <div className={`h-full ${meta.bar} rounded-full transition-all`} style={{ width: `${Math.max(score, 2)}%` }} />
      </div>
    </div>
  );
}

/** Tres iconos dicen más sobre la suplantación que un número de 0 a 100. */
function ImpersonationCell({ threat }: { threat: Threat }) {
  const score = threat.impersonationScore ?? threat.metadata?.impersonationScore ?? 0;
  const ca = threat.metadata?.contentAnalysis;
  if (!score) {
    return <span className="text-xs text-gray-600" title="Sin evidencia de suplantación activa">sin copia</span>;
  }
  const marks: { icon: string; label: string }[] = [];
  if (ca?.hasLoginForm) marks.push({ icon: '', label: 'Formulario de credenciales' });
  if (ca?.hasPaymentForm) marks.push({ icon: '', label: 'Pasarela de pago falsa' });
  if (ca?.hasBrandName) marks.push({ icon: '', label: 'Marca detectada en la página' });
  if (ca?.hasCopiedContent) marks.push({ icon: '', label: 'Contenido clonado del original' });
  return (
    <div className="flex items-center gap-2">
      <span className="text-orange-400 font-bold text-xs tabular-nums">{score}</span>
      <span className="flex gap-0.5">
        {marks.map(m => <span key={m.icon} title={m.label} className="text-sm">{m.icon}</span>)}
      </span>
    </div>
  );
}

/** Tiempo relativo: "hace 2 h" se lee más rápido que una fecha. */
function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const min = Math.floor(diff / 60000);
  if (min < 1) return 'ahora mismo';
  if (min < 60) return `hace ${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `hace ${h} h`;
  const d = Math.floor(h / 24);
  if (d < 30) return `hace ${d} d`;
  return new Date(iso).toLocaleDateString('es-CO');
}

function Modal({ title, onClose, children, wide }: { title: string; onClose: () => void; children: React.ReactNode; wide?: boolean }) {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4">
      <div className={`bg-gray-900 border border-gray-700 rounded-lg p-6 w-full ${wide ? 'max-w-3xl' : 'max-w-lg'} max-h-[90vh] overflow-y-auto`}>
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-bold">{title}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white text-xl">✕</button>
        </div>
        {children}
      </div>
    </div>
  );
}

export default function App() {
  const { user, logout } = useAuth();
  // Un usuario del cliente solo tiene alcance sobre su histórico y su postura.
  const esCliente = !!user?.companyId;
  const [threats, setThreats] = useState<Threat[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [activeTab, setActiveTab] = useState(esCliente ? 'historico' : 'dashboard');
  const [showCompanyModal, setShowCompanyModal] = useState(false);
  const [editingCompany, setEditingCompany] = useState<Company | null>(null);
  const [companyForm, setCompanyForm] = useState({
    name: '', domain: '', industry: '', contactEmail: '',
    adminEmail: '', alertEmailsStr: '', scanEnabled: true, scanFrequency: 24
  });
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [scanning, setScanning] = useState<string | null>(null);
  const [scanState, setScanState] = useState<string | null>(null);
  const [selectedThreat, setSelectedThreat] = useState<Threat | null>(null);
  const [selectedCompanyScans, setSelectedCompanyScans] = useState<{ company: Company; scans: DomainScan[] } | null>(null);
  const [executives, setExecutives] = useState<Executive[]>([]);
  const [reviewQueue, setReviewQueue] = useState<ExecutiveProfile[]>([]);
  const [platformPolicy, setPlatformPolicy] = useState<any>(null);
  const [scanningExec, setScanningExec] = useState<string | null>(null);

  // Alta y edición de directivos. La vigilancia de personas es el diferenciador
  // del producto y hasta ahora no se podía usar: la API existía, el formulario no.
  const [execForm, setExecForm] = useState<{
    id?: string; name: string; email: string; position: string; companyId: string;
    aliasesStr: string; consentRef: string; profilesStr: string; photoRef: string;
  } | null>(null);
  const [savingExec, setSavingExec] = useState(false);

  // Cuentas corporativas en redes: la tercera pata de la cobertura, junto al
  // dominio y a las personas.
  const [brandProfiles, setBrandProfiles] = useState<any[]>([]);
  const [scanningBrand, setScanningBrand] = useState<string | null>(null);
  const [oficialForm, setOficialForm] = useState<{ companyId: string; platform: string; handle: string } | null>(null);

  const fetchBrandProfiles = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/brand-social`);
      setBrandProfiles(Array.isArray(r.data) ? r.data : []);
    } catch { setBrandProfiles([]); }
  }, []);

  const scanBrand = async (companyId: string, nombre: string) => {
    setScanningBrand(companyId);
    try {
      const r = await axios.post(`${API}/brand-social/${companyId}/scan`);
      const d = r.data ?? {};
      await fetchBrandProfiles();
      await fetchOverview(filterCompany, companies);
      alert(
        `Barrido de redes de ${nombre}:\n\n` +
        `${d.candidatos ?? 0} handles candidatos en ${d.plataformas ?? 0} plataformas\n` +
        `${d.consultados ?? 0} consultados automáticamente\n` +
        `${d.registrados ?? 0} registrados como sospechosos\n` +
        `${d.encolados ?? 0} a revisión humana (la plataforma prohíbe la consulta automática)`
      );
    } catch (error: any) {
      alert(error?.response?.data?.error || 'No se pudo barrer las redes');
    }
    setScanningBrand(null);
  };

  const guardarOficial = async () => {
    if (!oficialForm?.handle.trim()) return;
    try {
      await axios.post(`${API}/brand-social/${oficialForm.companyId}/oficial`, {
        platform: oficialForm.platform, handle: oficialForm.handle
      });
      setOficialForm(null);
      await fetchBrandProfiles();
    } catch (error: any) {
      alert(error?.response?.data?.error || 'No se pudo registrar la cuenta oficial');
    }
  };

  const revisarCuentaMarca = async (id: string, decision: string) => {
    try {
      await axios.post(`${API}/brand-social/profile/${id}/review`, { decision });
      await fetchBrandProfiles();
      await fetchOverview(filterCompany, companies);
    } catch (error: any) {
      alert(error?.response?.data?.error || 'No se pudo registrar la decisión');
    }
  };

  const abrirNuevoDirectivo = () => setExecForm({
    name: '', email: '', position: '', companyId: companies[0]?.id ?? '',
    aliasesStr: '', consentRef: '', profilesStr: '', photoRef: ''
  });

  const abrirEditarDirectivo = (e: any) => setExecForm({
    id: e.id, name: e.name ?? '', email: e.email ?? '', position: e.position ?? '',
    companyId: e.companyId ?? e.company?.id ?? '',
    aliasesStr: (e.aliases ?? []).join(', '),
    consentRef: e.consentRef ?? '',
    profilesStr: (e.profiles ?? []).filter((p: any) => p.status === 'LEGITIMATE').map((p: any) => p.url).join('\n'),
    photoRef: e.photoRef ?? ''
  });

  const guardarDirectivo = async () => {
    if (!execForm || !execForm.name.trim() || !execForm.companyId) return;
    setSavingExec(true);
    try {
      const cuerpo = {
        name: execForm.name,
        email: execForm.email,
        position: execForm.position,
        companyId: execForm.companyId,
        aliases: execForm.aliasesStr.split(',').map(a => a.trim()).filter(Boolean),
        consentRef: execForm.consentRef,
        photoRef: execForm.photoRef,
        knownProfiles: execForm.profilesStr.split(/[\n,]/).map(u => u.trim()).filter(Boolean)
      };
      if (execForm.id) await axios.put(`${API}/executives/${execForm.id}`, cuerpo);
      else await axios.post(`${API}/executives`, cuerpo);
      setExecForm(null);
      await fetchExecutives();
      await fetchOverview(filterCompany, companies);
    } catch (error: any) {
      alert(error?.response?.data?.message || 'No se pudo guardar el directivo');
    }
    setSavingExec(false);
  };

  const eliminarDirectivo = async (id: string, nombre: string) => {
    if (!window.confirm(`¿Retirar a ${nombre} de la vigilancia? Se borran también sus perfiles registrados.`)) return;
    try {
      await axios.delete(`${API}/executives/${id}`);
      await fetchExecutives();
      await fetchOverview(filterCompany, companies);
    } catch (error: any) {
      alert(error?.response?.data?.message || 'No se pudo eliminar');
    }
  };
  const [reports, setReports] = useState<AuthorityReport[]>([]);
  const [authorities, setAuthorities] = useState<any>(null);
  const [openReport, setOpenReport] = useState<AuthorityReport | null>(null);
  const [domain, setDomain] = useState('');
  const [companyId, setCompanyId] = useState('');
  const [analyzing, setAnalyzing] = useState(false);
  const [result, setResult] = useState<any>(null);

  const [filterCompany, setFilterCompany] = useState('');
  const [overview, setOverview] = useState<Overview | null>(null);

  // Constancias de vigilancia. Se emiten solo cuando alguien las pide: no hay
  // ningún camino en la interfaz que las genere como efecto secundario.
  const [certs, setCerts] = useState<any[]>([]);
  const [certForm, setCertForm] = useState<{
    companyId: string; desde: string; hasta: string;
    solicitadaPor: string; destinatario: string; aviso?: string;
  } | null>(null);
  const [emitiendo, setEmitiendo] = useState(false);

  const fetchCerts = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/certificates`);
      setCerts(Array.isArray(r.data) ? r.data : []);
    } catch { setCerts([]); }
  }, []);

  const abrirEmision = async (companyId: string) => {
    const iso = (d: Date) => d.toISOString().slice(0, 10);
    const hoy = new Date();
    // Se propone el periodo que DE VERDAD tiene barridos. Proponer "el mes
    // pasado" a ciegas lleva a pedir una constancia de un periodo sin
    // vigilancia, que el sistema rechaza, con razón.
    let desde = iso(new Date(hoy.getFullYear(), hoy.getMonth() - 1, 1));
    let hasta = iso(new Date(hoy.getFullYear(), hoy.getMonth(), 0));
    let aviso: string | undefined;
    try {
      const r = await axios.get(`${API}/certificates/periodo-disponible/${companyId}`);
      if (r.data?.hayVigilancia) {
        desde = r.data.desde.slice(0, 10);
        hasta = iso(hoy);
        aviso = `${r.data.barridos} ${r.data.barridos === 1 ? 'barrido registrado' : 'barridos registrados'} desde el ${new Date(r.data.desde).toLocaleDateString('es-CO')}.`;
      } else {
        aviso = r.data?.mensaje;
      }
    } catch { /* se propone el mes anterior */ }
    setCertForm({ companyId, desde, hasta, solicitadaPor: '', destinatario: '', aviso });
  };

  const emitirConstancia = async () => {
    if (!certForm) return;
    if (certForm.solicitadaPor.trim().length < 3) {
      alert('Escribe quién solicita la constancia: queda registrado en el documento.');
      return;
    }
    setEmitiendo(true);
    try {
      const r = await axios.post(`${API}/certificates`, {
        companyId: certForm.companyId,
        desde: certForm.desde,
        // El periodo se acredita hasta el final del día indicado.
        hasta: new Date(`${certForm.hasta}T23:59:59`).toISOString(),
        solicitadaPor: certForm.solicitadaPor,
        destinatario: certForm.destinatario
      });
      setCertForm(null);
      await fetchCerts();
      if (r.data?.aviso) alert(r.data.aviso);
      else if (r.data?.pdf) openPdf(`${API.replace(/\/api$/, '')}${r.data.pdf}`);
    } catch (error: any) {
      alert(error?.response?.data?.error || 'No se pudo emitir la constancia');
    }
    setEmitiendo(false);
  };

  const fetchData = useCallback(async () => {
    if (esCliente) return;
    try {
      const [t, c] = await Promise.all([
        axios.get(`${API}/threats`),
        axios.get(`${API}/companies`)
      ]);
      setThreats(t.data.data);
      setCompanies(c.data.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  }, [esCliente]);

  /**
   * El estado general se pide para la empresa seleccionada.
   *
   * Antes se pedía siempre completo: el selector filtraba la tabla y dejaba
   * intactos la postura, el mapa y el acta, así que saltar de empresa a empresa
   * no cambiaba nada arriba. Una pantalla donde un control mueve media vista y
   * la otra media no, enseña a desconfiar del control.
   */
  const fetchOverview = useCallback(async (nombreEmpresa: string, lista: Company[]) => {
    if (esCliente) return;
    const id = nombreEmpresa ? lista.find(c => c.name === nombreEmpresa)?.id : undefined;
    try {
      const r = await axios.get(`${API}/insights/overview`, {
        params: id ? { companyId: id } : {}
      });
      setOverview(r.data);
    } catch {
      setOverview(null);
    }
  }, [esCliente]);

  const fetchExecutives = useCallback(async () => {
    try {
      const [e, q, p] = await Promise.all([
        axios.get(`${API}/executives`),
        axios.get(`${API}/executive-monitoring/review-queue`),
        axios.get(`${API}/executive-monitoring/policy`)
      ]);
      setExecutives(e.data.data ?? e.data ?? []);
      setReviewQueue(q.data ?? []);
      setPlatformPolicy(p.data ?? null);
    } catch (error) {
      console.error('Error cargando directivos:', error);
    }
  }, []);

  useEffect(() => {
    if (activeTab === 'executives') { fetchExecutives(); fetchBrandProfiles(); }
  }, [activeTab, fetchExecutives, fetchBrandProfiles]);

  const fetchReports = useCallback(async () => {
    try {
      const [p, a] = await Promise.all([
        axios.get(`${API}/reports/pending`),
        axios.get(`${API}/reports/authorities`)
      ]);
      setReports(p.data ?? []);
      setAuthorities(a.data ?? null);
    } catch (error) {
      console.error('Error cargando reportes:', error);
    }
  }, []);

  useEffect(() => {
    if (activeTab === 'reports') { fetchReports(); fetchCerts(); }
  }, [activeTab, fetchReports, fetchCerts]);

  const approveAndCopy = async (report: AuthorityReport) => {
    try {
      // El aprobador lo fija el servidor a partir de la sesión: no se puede firmar como otro.
      await axios.post(`${API}/reports/${report.id}/approve`, {});
      try { await navigator.clipboard.writeText(report.body); } catch { /* sin permiso de portapapeles */ }
      setOpenReport(null);
      await fetchReports();
    } catch (error: any) {
      alert(error?.response?.data?.error || 'No se pudo aprobar el reporte');
    }
  };

  const scanExecutive = async (id: string) => {
    setScanningExec(id);
    try {
      await axios.post(`${API}/executive-monitoring/${id}/scan`);
      await fetchExecutives();
    } catch (error: any) {
      alert(error?.response?.data?.error || 'No se pudo iniciar la vigilancia');
    }
    setScanningExec(null);
  };

  const reviewProfile = async (profileId: string, decision: string) => {
    try {
      await axios.post(`${API}/executive-monitoring/profiles/${profileId}/review`, {
        decision
      });
      await fetchExecutives();
    } catch (error) {
      console.error('Error registrando la decisión:', error);
    }
  };

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [fetchData]);

  // Se recarga al cambiar de empresa y con cada refresco de la lista.
  useEffect(() => {
    fetchOverview(filterCompany, companies);
    const interval = setInterval(() => fetchOverview(filterCompany, companies), 30000);
    return () => clearInterval(interval);
  }, [filterCompany, companies, fetchOverview]);

  /**
   * Sigue un barrido encolado hasta que termina, venga del alta de la empresa
   * o del botón. Un mismo trabajo no debería informarse de dos formas según
   * por dónde se lanzó.
   */
  const seguirBarrido = (jobId: string, companyName: string) => {
    const inicio = Date.now();
    const legible: Record<string, string> = {
      waiting: 'en cola', delayed: 'en cola', active: 'barriendo',
      completed: 'terminado', failed: 'falló'
    };

    const sondear = async () => {
      try {
        const st = await axios.get(`${API}/queue/status/${jobId}`);
        const estado = st.data?.data?.state as string;
        const min = Math.floor((Date.now() - inicio) / 60000);
        setScanState(`${legible[estado] ?? estado}${min > 0 ? ` · ${min} min` : ''}`);

        if (estado === 'completed' || estado === 'failed') {
          await fetchData();
          setScanning(null);
          setScanState(null);
          if (estado === 'failed') {
            const motivo = st.data?.data?.failedReason;
            alert(
              `El barrido de ${companyName} falló.\n\n` +
              (motivo ? `Motivo: ${motivo}` : 'El trabajo no registró un motivo.')
            );
          }
          return;
        }
      } catch { /* el worker puede tardar en registrar el trabajo */ }

      // Techo de 20 minutos: a partir de ahí el aviso lo da el log, no la interfaz.
      if (Date.now() - inicio > 20 * 60 * 1000) {
        await fetchData(); setScanning(null); setScanState(null);
        return;
      }
      setTimeout(sondear, 4000);
    };
    setTimeout(sondear, 2500);
  };

  /**
   * Lanza el barrido y SIGUE el trabajo hasta que termina.
   *
   * Antes esperaba 60 segundos a ciegas y refrescaba. Un barrido real tarda
   * lo que tarda, minutos, según el tamaño del espacio de candidatos,  así que
   * el usuario veía "listo" cuando el trabajo seguía corriendo, y la pantalla
   * vacía le hacía creer que no había pasado nada. Esperar sin saber es peor
   * que esperar sabiendo.
   */
  const startProactiveScan = async (cId: string, companyName: string) => {
    setScanning(cId);
    setScanState('encolando…');
    try {
      const r = await axios.post(`${API}/queue/scan/${cId}`);
      const jobId = r.data?.data?.jobId;
      if (!jobId) { setTimeout(() => { fetchData(); setScanning(null); setScanState(null); }, 60000); return; }

      seguirBarrido(jobId, companyName);
    } catch (error) {
      console.error('Error iniciando scan:', error);
      setScanning(null);
      setScanState(null);
      alert('No se pudo encolar el barrido. ¿Está Redis arriba?');
    }
  };

  const viewCompanyScans = async (company: Company) => {
    try {
      const res = await axios.get(`${API}/companies/${company.id}/scans`);
      setSelectedCompanyScans({ company, scans: res.data.data });
    } catch (error) {
      console.error('Error fetching scans:', error);
    }
  };

  const toggleScanEnabled = async (company: Company) => {
    try {
      await axios.put(`${API}/companies/${company.id}`, { scanEnabled: !company.scanEnabled });
      fetchData();
    } catch (error) {
      console.error('Error toggling scan:', error);
    }
  };

  const openCreateCompany = () => {
    setEditingCompany(null);
    setCompanyForm({ name: '', domain: '', industry: '', contactEmail: '', adminEmail: '', alertEmailsStr: '', scanEnabled: true, scanFrequency: 24 });
    setShowCompanyModal(true);
  };

  const openEditCompany = (company: Company) => {
    setEditingCompany(company);
    setCompanyForm({
      name: company.name,
      domain: company.domain,
      industry: company.industry || '',
      contactEmail: company.contactEmail || '',
      adminEmail: company.adminEmail || '',
      alertEmailsStr: (company.alertEmails || []).join(', '),
      scanEnabled: company.scanEnabled,
      scanFrequency: company.scanFrequency || 24
    });
    setShowCompanyModal(true);
  };

  const saveCompany = async () => {
    if (!companyForm.name || !companyForm.domain) return;
    setSaving(true);
    try {
      const alertEmails = companyForm.alertEmailsStr
        .split(',').map(e => e.trim()).filter(e => e.length > 0);
      const data = {
        name: companyForm.name,
        domain: companyForm.domain,
        industry: companyForm.industry,
        contactEmail: companyForm.contactEmail,
        adminEmail: companyForm.adminEmail,
        alertEmails,
        scanEnabled: companyForm.scanEnabled,
        scanFrequency: companyForm.scanFrequency
      };
      if (editingCompany) {
        await axios.put(`${API}/companies/${editingCompany.id}`, data);
      } else {
        const r = await axios.post(`${API}/companies`, data);
        const nueva = r.data?.data;
        const inicial = r.data?.barridoInicial;
        setShowCompanyModal(false);
        await fetchData();
        setSaving(false);

        // El alta dispara el primer barrido. Verlo correr en el primer minuto
        // es lo que convierte un formulario en un servicio.
        if (inicial?.encolado && nueva?.id) {
          setActiveTab('admin');
          setScanning(nueva.id);
          setScanState('encolando…');
          seguirBarrido(inicial.jobId, nueva.name);
        } else if (inicial?.motivo) {
          alert(`${nueva?.name} quedó registrada. ${inicial.motivo}`);
        }
        return;
      }
      setShowCompanyModal(false);
      fetchData();
    } catch (error: any) {
      alert(error?.response?.data?.message || 'No se pudo guardar la empresa');
    }
    setSaving(false);
  };

  const deleteCompany = async (id: string) => {
    try {
      await axios.delete(`${API}/companies/${id}`);
      setDeleteConfirm(null);
      fetchData();
    } catch (error) {
      console.error('Error deleting company:', error);
    }
  };

  const analyzeDomain = async () => {
    if (!domain || !companyId) return;
    setAnalyzing(true);
    setResult(null);
    try {
      const res = await axios.post(`${API}/detection/verify`, { domain, companyId });
      setResult(res.data);
      fetchData();
    } catch (error: any) {
      setResult({ error: error?.response?.data?.error || 'No se pudo verificar el dominio. Revisa que el backend esté arriba.' });
    }
    setAnalyzing(false);
  };

  const [severityFilter, setSeverityFilter] = useState<string>('');
  // Útil la primera semana, estorba la cuarta: se recuerda por usuario.
  const [showGuide, setShowGuide] = useState<boolean>(() => {
    try { return localStorage.getItem('verax.showGuide') !== 'false'; } catch { return true; }
  });
  useEffect(() => {
    try { localStorage.setItem('verax.showGuide', String(showGuide)); } catch { /* modo privado */ }
  }, [showGuide]);

  const companyThreats = filterCompany ? threats.filter(t => t.company?.name === filterCompany) : threats;
  const critical = companyThreats.filter(t => severityOf(t.score, t.severity) === 'CRITICAL').length;
  const high = companyThreats.filter(t => severityOf(t.score, t.severity) === 'HIGH').length;
  const active = companyThreats.filter(t => t.status === 'ACTIVE').length;

  // Los KPI son filtros: un número que no lleva a ningún lado es un número muerto.
  const filteredThreats = companyThreats.filter(t => {
    if (severityFilter === 'ACTIVE') return t.status === 'ACTIVE';
    if (severityFilter) return severityOf(t.score, t.severity) === severityFilter;
    return true;
  });

  // Estado del barrido: "0 amenazas" sin fecha se lee como sistema caído,
  // no como buena noticia.
  const scannedCompanies = companies.filter(c => c.lastScannedAt);
  const lastScan = scannedCompanies.length
    ? new Date(Math.max(...scannedCompanies.map(c => new Date(c.lastScannedAt as string).getTime())))
    : null;
  const nextScanHours = companies.length ? (companies[0].scanFrequency || 4) : 4;
  const nextScanAt = lastScan ? new Date(lastScan.getTime() + nextScanHours * 3600 * 1000) : null;
  const pending = companyThreats.filter(t => t.status === 'ACTIVE' || t.status === 'INVESTIGATING').length;

  // Orden por defecto: severidad, luego recencia. Nunca alfabético.
  const SEVERITY_RANK: Record<string, number> = { CRITICAL: 0, HIGH: 1, MEDIUM: 2, LOW: 3, SAFE: 4 };
  const sortedThreats = [...filteredThreats].sort((a, b) => {
    const d = SEVERITY_RANK[severityOf(a.score, a.severity)] - SEVERITY_RANK[severityOf(b.score, b.severity)];
    if (d !== 0) return d;
    return new Date(b.detectedAt).getTime() - new Date(a.detectedAt).getTime();
  });

  const tabs = [
    { id: 'dashboard', label: 'Resumen', show: !esCliente },
    { id: 'historico', label: 'Casos', show: true },
    { id: 'verificacion', label: 'Verificación', show: true },
    { id: 'analyze', label: 'Analizar', show: !esCliente && atLeast(user, 'ANALYST') },
    { id: 'threats', label: 'Amenazas', show: !esCliente },
    { id: 'executives', label: 'Identidad en redes', show: !esCliente },
    { id: 'reports', label: 'Reportes', show: !esCliente },
    { id: 'intel', label: 'Inteligencia', show: !esCliente && atLeast(user, 'ANALYST') },
    { id: 'admin', label: 'Administración', show: atLeast(user, 'ADMIN') },
  ].filter(t => t.show);

  return (
    <div className="min-h-screen bg-gray-950 text-white">
      <header className="bg-gray-900 border-b border-gray-800 px-6">
        <div className="flex items-center justify-between py-3">
          <div className="flex items-center gap-3">
            <img src="/verax-mark.svg" alt="" width={34} height={34} className="rounded-lg" />
            <div>
              <h1 className="text-xl font-bold tracking-[0.18em]">VERAX</h1>
              <p className="text-xs text-gray-400">Detección de suplantación de marca y directivos</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right leading-tight">
              <p className="text-sm text-gray-200">{user?.name}</p>
              <p className="text-[11px] text-gray-500">{user ? ROLE_LABEL[user.role] : ''}</p>
            </div>
            <button onClick={logout} className="text-xs text-gray-300 hover:text-white border border-gray-700 rounded px-3 py-1.5">Salir</button>
          </div>
        </div>
        <nav aria-label="Secciones" className="flex gap-1 overflow-x-auto -mb-px">
          {tabs.map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)} aria-current={activeTab === tab.id ? 'page' : undefined}
              className={`px-4 py-2.5 text-sm whitespace-nowrap border-b-2 transition-colors ${activeTab === tab.id ? 'border-cyan-400 text-white font-medium' : 'border-transparent text-gray-400 hover:text-gray-200'}`}>
              {tab.label}
            </button>
          ))}
        </nav>
      </header>

      <main className="p-6 max-w-7xl mx-auto">

        <PageHelp key={activeTab} id={activeTab} />
        {activeTab === 'historico' && <HistoryPanel />}
        {activeTab === 'verificacion' && <VerificationPanel onReportes={esCliente ? undefined : () => setActiveTab('reports')} />}
        {activeTab === 'intel' && <IntelligencePanel puedeDecidir={atLeast(user, 'ADMIN')} />}

        {activeTab === 'dashboard' && (
          <div>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold">Resumen</h2>
              <div className="flex items-center gap-3">                <select value={filterCompany} onChange={e => setFilterCompany(e.target.value)}                  className="bg-gray-800 border border-gray-700 rounded px-3 py-1.5 text-sm text-white focus:outline-none focus:border-blue-500">                  <option value="">Todas las empresas</option>                  {companies.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}                </select>                <span className="text-xs text-gray-500">Auto-actualiza cada 30s</span>              </div>
            </div>
            {/* Franja de estado: la prueba de diseño es que un gerente no técnico
                entienda en diez segundos si su empresa está bien o mal hoy. */}
            <div className={`mb-6 rounded-lg border px-5 py-4 ${
              critical > 0
                ? 'bg-red-950/40 border-red-800'
                : pending > 0
                  ? 'bg-yellow-950/30 border-yellow-800/60'
                  : 'bg-green-950/25 border-green-900/60'
            }`}>
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                  <p className="text-base font-semibold">
                    {critical > 0
                      ? `${critical} ${critical === 1 ? 'ataque activo suplantando tu marca' : 'ataques activos suplantando tu marca'}. Revísalo ahora.`
                      : pending > 0
                        ? `▲ ${pending} ${pending === 1 ? 'amenaza espera' : 'amenazas esperan'} tu decisión.`
                        : '✓ Tu marca está bajo vigilancia. Sin amenazas pendientes.'}
                  </p>
                  <p className="text-xs text-gray-400 mt-1">
                    {lastScan
                      ? <>Último barrido: {timeAgo(lastScan.toISOString())}
                          {nextScanAt && <> · Próximo: {nextScanAt > new Date() ? `en ${Math.max(1, Math.round((nextScanAt.getTime() - Date.now()) / 60000))} min` : 'en curso'}</>}
                        </>
                      : 'Aún no se ha ejecutado el primer barrido.'}
                  </p>
                </div>
                {critical > 0 && (
                  <button onClick={() => setSeverityFilter('CRITICAL')}
                    className="bg-red-600 hover:bg-red-700 px-4 py-2 rounded text-sm font-semibold whitespace-nowrap">
                    Ver amenazas críticas
                  </button>
                )}
              </div>
            </div>

            {/* Los KPI son filtros, no adornos. */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              {[
                { key: '',         label: 'Total amenazas', value: companyThreats.length, color: 'border-blue-500' },
                { key: 'ACTIVE',   label: 'Activas',        value: active,   color: 'border-yellow-500' },
                { key: 'CRITICAL', label: 'Críticas',       value: critical, color: 'border-red-500' },
                { key: 'HIGH',     label: 'High',           value: high,     color: 'border-orange-500' },
              ].map(card => (
                <button key={card.label} onClick={() => setSeverityFilter(card.key)}
                  className={`text-left bg-gray-900 border-l-4 ${card.color} p-4 rounded transition-colors hover:bg-gray-800 ${
                    severityFilter === card.key ? 'ring-1 ring-blue-500 bg-gray-800' : ''
                  }`}>
                  <p className="text-gray-400 text-sm">{card.label}</p>
                  <p className="text-3xl font-bold mt-1 tabular-nums">{card.value}</p>
                  <p className="text-[11px] text-gray-600 mt-1">
                    {severityFilter === card.key ? 'filtrando por esto' : 'clic para filtrar'}
                  </p>
                </button>
              ))}
            </div>
            {/* Cómo está la organización: marcas y personas, con semáforo.
                Es lo que se muestra en comité; la tabla de abajo es la
                operación diaria del analista. */}
            {overview && (
              <>
                <h3 className="text-lg font-semibold mb-3">Cómo está tu organización hoy</h3>
                <PosturePanel
                  overview={overview}
                  onMarca={(nombre) => setFilterCompany(nombre)}
                  onDirectivos={() => setActiveTab('executives')}
                />
                <OriginMap origen={overview.origen} />
                <ScanReceiptPanel actas={overview.actas} hayAmenazas={companyThreats.length > 0} />
              </>
            )}

            <div className="bg-gray-900 rounded p-4 mb-6 border border-gray-800">
              <button onClick={() => setShowGuide(g => !g)}
                className="w-full flex items-center justify-between text-sm font-semibold text-gray-300 mb-3">
                <span>Guía de scores</span>
                <span className="text-xs text-gray-500 font-normal">{showGuide ? 'ocultar' : 'mostrar'}</span>
              </button>
              {showGuide && (<>
              <div className="grid grid-cols-5 gap-2 text-xs text-center">
                <div className="bg-green-900 rounded p-2"><p className="font-bold text-green-400">SAFE 0-19</p><p className="text-gray-400 mt-1">Sin evidencia de amenaza</p></div>
                <div className="bg-blue-900 rounded p-2"><p className="font-bold text-blue-400">LOW 20-39</p><p className="text-gray-400 mt-1">Sospechoso, monitorear</p></div>
                <div className="bg-yellow-900 rounded p-2"><p className="font-bold text-yellow-400">MEDIUM 40-59</p><p className="text-gray-400 mt-1">Probable amenaza</p></div>
                <div className="bg-orange-900 rounded p-2"><p className="font-bold text-orange-400">HIGH 60-79</p><p className="text-gray-400 mt-1">Amenaza confirmada</p></div>
                <div className="bg-red-900 rounded p-2"><p className="font-bold text-red-400">CRITICAL 80+</p><p className="text-gray-400 mt-1">Ataque activo</p></div>
              </div>
              <p className="text-xs text-gray-500 mt-3"><strong className="text-gray-400">Suplantación:</strong> indica si la página copia activamente la marca, tiene formularios de login o pasarelas de pago falsas. Score 0 = sin evidencia de suplantación activa. <span className="text-gray-600">CRITICAL exige evidencia de captura activa de datos: la sola similitud de nombre nunca llega a 80.</span></p>
              </>)}
            </div>
            <h3 className="text-lg font-semibold mb-4">Amenazas Recientes</h3>
            <div className="bg-gray-900 rounded overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-800">
                  <tr>
                    {['Dominio malicioso', 'Empresa', 'Tipo', 'Amenaza', 'Suplantación', 'Próxima revisión', 'Email', 'Detectado', ''].map(h => (
                      <th key={h} className="text-left px-4 py-3 text-xs text-gray-400 uppercase">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {sortedThreats.slice(0, 15).map(threat => (
                    <tr key={threat.id} onClick={() => setSelectedThreat(threat)}
                      className="border-t border-gray-800 hover:bg-gray-800 cursor-pointer">
                      <td className="px-4 py-3 font-mono text-sm text-red-400">{threat.domain}</td>
                      <td className="px-4 py-3 text-sm">{threat.company?.name}</td>
                      <td className="px-4 py-3 text-xs text-gray-400">{threat.metadata?.variantType || threat.type}</td>
                      <td className="px-4 py-3"><ScoreBadge score={threat.score} severity={threat.severity} /></td>
                      <td className="px-4 py-3"><ImpersonationCell threat={threat} /></td>
                      <td className="px-4 py-3 text-xs text-gray-500">
                        {threat.nextCheckAt
                          ? (new Date(threat.nextCheckAt) <= new Date()
                              ? <span className="text-blue-400">en cola</span>
                              : `en ${Math.max(1, Math.round((new Date(threat.nextCheckAt).getTime() - Date.now()) / 60000))} min`)
                          : '-'}
                      </td>
                      <td className="px-4 py-3 text-xs">
                        {threat.emailSent ? <span className="text-green-400" title="Alerta enviada">✓</span> : <span className="text-gray-600" title="Sin alerta enviada">-</span>}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500" title={new Date(threat.detectedAt).toLocaleString('es-CO')}>
                        {timeAgo(threat.detectedAt)}
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-xs bg-blue-600 hover:bg-blue-700 px-3 py-1.5 rounded font-medium whitespace-nowrap">Revisar</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {sortedThreats.length === 0 && (
                <div className="text-center py-12 text-gray-500">
                  {severityFilter ? (
                    <>
                      <p className="text-gray-400">Ninguna amenaza en este filtro.</p>
                      <button onClick={() => setSeverityFilter('')} className="text-blue-400 text-sm mt-2 hover:underline">Ver todas</button>
                    </>
                  ) : (
                    <>
                      <p className="text-gray-300">Sin amenazas detectadas.</p>
                      <p className="text-xs mt-2">
                        {lastScan
                          ? `El último barrido terminó ${timeAgo(lastScan.toISOString())} y no encontró suplantaciones.`
                          : 'Registra una empresa en Administración para iniciar la vigilancia.'}
                      </p>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'executives' && (
          <div>
            <div className="flex justify-between items-start mb-6 flex-wrap gap-3">
              <div>
                <h2 className="text-2xl font-bold">Identidad en redes sociales</h2>
                <p className="text-gray-400 text-sm mt-1 max-w-3xl">
                  Cuentas que suplantan a la empresa o a sus directivos. Son dos fraudes distintos:
                  la cuenta corporativa falsa atiende a los clientes reales; el perfil de un
                  directivo sirve para pedir una transferencia urgente.
                </p>
              </div>
              <button onClick={() => { fetchExecutives(); fetchBrandProfiles(); }}
                className="text-sm bg-gray-800 hover:bg-gray-700 px-4 py-2 rounded">Actualizar</button>
            </div>

            {/* ── Cuentas de la empresa ─────────────────────────────────── */}
            <div className="mb-10">
              <div className="flex items-center justify-between mb-1 flex-wrap gap-2">
                <h3 className="text-lg font-semibold">Cuentas de la empresa</h3>
              </div>
              <p className="text-xs text-gray-500 mb-3 max-w-3xl">
                Una cuenta corporativa falsa no espera a que la víctima llegue por un enlace:
                responde a quien se queja en público, se lleva la conversación a un privado y pide
                datos «para resolver el caso». El cliente no sospecha porque escribió él primero.
              </p>

              <div className="bg-gray-900 border border-gray-800 rounded p-4 mb-4 flex flex-wrap gap-2">
                {companies.map(c => (
                  <div key={c.id} className="flex gap-1.5">
                    <button onClick={() => scanBrand(c.id, c.name)} disabled={scanningBrand === c.id}
                      className="text-xs bg-blue-600 hover:bg-blue-700 disabled:bg-gray-700 px-3 py-2 rounded font-medium">
                      {scanningBrand === c.id ? `Barriendo ${c.name}…` : `Barrer redes de ${c.name}`}
                    </button>
                    <button onClick={() => setOficialForm({ companyId: c.id, platform: 'X', handle: '' })}
                      title={`Registrar una cuenta oficial de ${c.name}`}
                      className="text-xs bg-gray-800 hover:bg-gray-700 px-3 py-2 rounded">+ oficial</button>
                  </div>
                ))}
                {companies.length === 0 && (
                  <p className="text-sm text-gray-500">Registra una empresa primero.</p>
                )}
              </div>

              {brandProfiles.length === 0 ? (
                <div className="bg-gray-900 border border-gray-800 rounded p-8 text-center text-gray-500">
                  <p className="text-gray-300">Ninguna cuenta registrada todavía.</p>
                  <p className="text-xs mt-2 max-w-xl mx-auto">
                    Registra primero las cuentas oficiales de cada marca: son la referencia contra la
                    que se compara todo lo que se encuentre. Sin ellas, cualquier cuenta parece sospechosa.
                  </p>
                </div>
              ) : (
                <div className="bg-gray-900 rounded overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-gray-800">
                      <tr>
                        {['Cuenta', 'Empresa', 'Estado', 'Suplantación', 'Señales', ''].map(h => (
                          <th key={h} className="text-left px-4 py-3 text-xs text-gray-400 uppercase">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {brandProfiles.map((p: any) => (
                        <tr key={p.id} className="border-t border-gray-800 align-top">
                          <td className="px-4 py-3">
                            <span className="text-[10px] bg-gray-800 px-1.5 py-0.5 rounded uppercase tracking-wide mr-2">
                              {p.platform}
                            </span>
                            <a href={p.url} target="_blank" rel="noreferrer"
                              className="font-mono text-sm text-blue-400 hover:underline">@{p.handle}</a>
                            {p.displayName && (
                              <span className="block text-[11px] text-gray-500 mt-0.5">{p.displayName}</span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-sm">{p.company?.name}</td>
                          <td className="px-4 py-3 text-xs">
                            {p.status === 'LEGITIMATE' ? <span className="text-green-400">✓ oficial</span>
                              : p.status === 'IMPERSONATION' ? <span className="text-red-400">suplantación</span>
                              : p.status === 'DISMISSED' ? <span className="text-gray-500">descartada</span>
                              : p.needsHumanReview ? <span className="text-yellow-400">▲ revisión humana</span>
                              : <span className="text-yellow-400">▲ sospechosa</span>}
                          </td>
                          <td className="px-4 py-3 text-sm tabular-nums">
                            {p.status === 'LEGITIMATE' ? '-' : `${p.impersonationScore}/100`}
                          </td>
                          <td className="px-4 py-3 text-[11px] text-gray-400 max-w-xs">
                            {(p.evidence ?? []).slice(0, 3).map((e: string, i: number) => (
                              <span key={i} className="block">· {e}</span>
                            ))}
                          </td>
                          <td className="px-4 py-3">
                            {p.status === 'SUSPECT' && (
                              <div className="flex gap-1.5 justify-end">
                                <button onClick={() => revisarCuentaMarca(p.id, 'IMPERSONATION')}
                                  className="text-xs bg-red-600 hover:bg-red-700 px-2.5 py-1.5 rounded whitespace-nowrap">Es suplantación</button>
                                <button onClick={() => revisarCuentaMarca(p.id, 'LEGITIMATE')}
                                  className="text-xs bg-gray-800 hover:bg-gray-700 px-2.5 py-1.5 rounded">Es nuestra</button>
                                <button onClick={() => revisarCuentaMarca(p.id, 'DISMISSED')}
                                  className="text-xs bg-gray-800 hover:bg-gray-700 px-2.5 py-1.5 rounded">Descartar</button>
                              </div>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Cola de revisión humana: lo primero, porque es lo accionable */}
            <div className="mb-8">
              <h3 className="text-lg font-semibold mb-1">
                Pendiente de tu revisión
                {reviewQueue.length > 0 && (
                  <span className="ml-2 text-xs bg-yellow-600 px-2 py-0.5 rounded-full align-middle">
                    {reviewQueue.length}
                  </span>
                )}
              </h3>
              <p className="text-xs text-gray-500 mb-3">
                Estas plataformas prohíben la verificación automatizada en sus términos de servicio.
                VERAX encuentra el candidato y te lo entrega; la decisión es tuya.
              </p>

              {reviewQueue.length === 0 ? (
                <div className="bg-gray-900 border border-gray-800 rounded p-8 text-center text-gray-500">
                  <p className="text-3xl mb-2">✓</p>
                  <p className="text-gray-300">Nada pendiente de revisión.</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {reviewQueue.slice(0, 25).map(profile => (
                    <div key={profile.id}
                      className="bg-gray-900 border border-gray-800 rounded p-4 flex items-start justify-between gap-4 flex-wrap">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-xs bg-gray-800 px-2 py-0.5 rounded uppercase tracking-wide">
                            {profile.platform}
                          </span>
                          <a href={profile.url} target="_blank" rel="noreferrer"
                            className="font-mono text-sm text-blue-400 hover:underline break-all">
                            @{profile.handle}
                          </a>
                        </div>
                        <p className="text-sm text-gray-300 mt-1">
                          Podría suplantar a <strong>{profile.executive?.name}</strong>
                          {profile.executive?.position && ` · ${profile.executive.position}`}
                          {profile.executive?.company?.name && ` · ${profile.executive.company.name}`}
                        </p>
                        <p className="text-[11px] text-gray-600 mt-1">
                          Detectado {timeAgo(profile.firstSeen)} · {profile.discoverySource.replace('HANDLE_VARIANT:', 'variante de handle: ')}
                        </p>
                      </div>
                      <div className="flex gap-2 shrink-0">
                        <a href={profile.url} target="_blank" rel="noreferrer"
                          className="text-xs bg-gray-800 hover:bg-gray-700 px-3 py-2 rounded">Abrir perfil</a>
                        <button onClick={() => reviewProfile(profile.id, 'IMPERSONATION')}
                          className="text-xs bg-red-600 hover:bg-red-700 px-3 py-2 rounded font-medium">
                          Es suplantación
                        </button>
                        <button onClick={() => reviewProfile(profile.id, 'DISMISSED')}
                          className="text-xs bg-gray-800 hover:bg-gray-700 px-3 py-2 rounded">
                          Descartar
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Personas vigiladas */}
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <h3 className="text-lg font-semibold">Personas vigiladas</h3>
              <button onClick={abrirNuevoDirectivo} disabled={companies.length === 0}
                title={companies.length === 0 ? 'Registra primero una empresa' : ''}
                className="text-sm bg-blue-600 hover:bg-blue-700 disabled:bg-gray-700 disabled:text-gray-500 px-4 py-2 rounded font-medium">
                + Nuevo directivo
              </button>
            </div>
            <div className="bg-gray-900 rounded overflow-hidden mb-8">
              <table className="w-full">
                <thead className="bg-gray-800">
                  <tr>
                    {['Nombre', 'Cargo', 'Empresa', 'Autorización', 'Línea base', ''].map(h => (
                      <th key={h} className="text-left px-4 py-3 text-xs text-gray-400 uppercase">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {executives.map(exec => (
                    <tr key={exec.id} className="border-t border-gray-800">
                      <td className="px-4 py-3 text-sm">{exec.name}</td>
                      <td className="px-4 py-3 text-sm text-gray-400">{exec.position || '-'}</td>
                      <td className="px-4 py-3 text-sm text-gray-400">{exec.company?.name || '-'}</td>
                      <td className="px-4 py-3 text-xs">
                        {exec.consentRef
                          ? <span className="text-green-400">✓ registrada</span>
                          : <span className="text-yellow-400" title="Ley 1581 de 2012: vigilar a una persona exige autorización documentada">
                              ▲ falta autorización
                            </span>}
                      </td>
                      <td className="px-4 py-3 text-xs">
                        {exec.photoPhash
                          ? <span className="text-green-400">✓ capturada</span>
                          : <span className="text-gray-500">sin foto de referencia</span>}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex gap-1.5 justify-end">
                          <button onClick={() => scanExecutive(exec.id)}
                            disabled={scanningExec === exec.id || !exec.consentRef}
                            title={!exec.consentRef ? 'Registra primero la autorización de tratamiento' : ''}
                            className="text-xs bg-blue-600 hover:bg-blue-700 disabled:bg-gray-700 disabled:text-gray-500 px-3 py-1.5 rounded whitespace-nowrap">
                            {scanningExec === exec.id ? 'Buscando…' : 'Buscar suplantaciones'}
                          </button>
                          <button onClick={() => abrirEditarDirectivo(exec)}
                            className="text-xs bg-gray-800 hover:bg-gray-700 px-3 py-1.5 rounded">Editar</button>
                          <button onClick={() => eliminarDirectivo(exec.id, exec.name)}
                            className="text-xs bg-gray-800 hover:bg-red-900 px-3 py-1.5 rounded">Quitar</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {executives.length === 0 && (
                <div className="text-center py-10 text-gray-500">
                  <p>No hay directivos registrados todavía.</p>
                </div>
              )}
            </div>

            {/* Política: es la respuesta a "¿esto es legal?" */}
            {platformPolicy && (
              <div className="bg-gray-900 border border-gray-800 rounded p-4">
                <p className="text-sm font-semibold text-gray-300 mb-3">Cómo se vigila cada plataforma</p>
                <div className="grid md:grid-cols-2 gap-3">
                  {platformPolicy.platforms?.map((p: any) => (
                    <div key={p.platform} className="bg-gray-950 rounded p-3 border border-gray-800">
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-sm font-medium">{p.label}</span>
                        <span className={`text-[11px] px-2 py-0.5 rounded ${
                          p.verification === 'automática' ? 'bg-green-900 text-green-300' : 'bg-yellow-900 text-yellow-300'
                        }`}>{p.verification}</span>
                      </div>
                      <p className="text-[11px] text-gray-500 mt-2 leading-relaxed">{p.reason}</p>
                    </div>
                  ))}
                </div>
                <ul className="mt-4 text-[11px] text-gray-500 space-y-1">
                  {platformPolicy.principles?.map((pr: string) => <li key={pr}>· {pr}</li>)}
                </ul>
              </div>
            )}
          </div>
        )}

        {activeTab === 'reports' && (
          <div>
            <div className="mb-6">
              <h2 className="text-2xl font-bold">Reportes a autoridades</h2>
              <p className="text-gray-400 text-sm mt-1">
                VERAX redacta el reporte y adjunta la evidencia. El envío lo autorizas tú.
              </p>
            </div>

            <div className="bg-gray-900 border border-gray-800 rounded p-4 mb-6">
              <p className="text-sm text-gray-300">
                Un reporte afirma ante una autoridad que un tercero identificable está
                cometiendo un delito. Por eso nada sale automáticamente: queda registrado
                quién aprobó, cuándo, y con qué evidencia.
              </p>
              {authorities?.note && (
                <p className="text-xs text-gray-500 mt-2">{authorities.note}</p>
              )}
            </div>

            {/* Constancias de vigilancia, se emiten a petición, nunca solas */}
            <div className="bg-gray-900 border border-gray-800 rounded mb-8 overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-800 flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <h3 className="font-semibold">Constancias de vigilancia</h3>
                  <p className="text-xs text-gray-500 mt-0.5 max-w-2xl">
                    El documento que acredita ante el cliente que su marca estuvo vigilada durante un
                    periodo y con qué resultado. Sirve igual cuando hubo hallazgos que cuando no, y
                    sobre todo cuando no. Se emite solo cuando alguien la pide, y queda registrado quién.
                  </p>
                </div>
              </div>

              <div className="px-4 py-3 border-b border-gray-800 flex flex-wrap gap-2">
                {companies.map(c => (
                  <button key={c.id} onClick={() => abrirEmision(c.id)}
                    className="text-xs bg-blue-600 hover:bg-blue-700 px-3 py-2 rounded font-medium">
                    Emitir para {c.name}
                  </button>
                ))}
                {companies.length === 0 && (
                  <p className="text-sm text-gray-500">Registra una empresa antes de emitir constancias.</p>
                )}
              </div>

              {certs.length === 0 ? (
                <p className="px-4 py-6 text-sm text-gray-500 text-center">
                  Todavía no se ha emitido ninguna constancia.
                </p>
              ) : (
                <table className="w-full">
                  <thead className="bg-gray-800">
                    <tr>
                      {['Folio', 'Marca', 'Periodo', 'Solicitada por', 'Emitida', ''].map(h => (
                        <th key={h} className="text-left px-4 py-2.5 text-xs text-gray-400 uppercase">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {certs.map(c => (
                      <tr key={c.id} className="border-t border-gray-800">
                        <td className="px-4 py-3 font-mono text-sm">
                          {c.folio}
                          {c.revokedAt && <span className="ml-2 text-xs text-red-400">revocada</span>}
                        </td>
                        <td className="px-4 py-3 text-sm">{c.company?.name}</td>
                        <td className="px-4 py-3 text-xs text-gray-400">
                          {new Date(c.periodStart).toLocaleDateString('es-CO')} al{' '}
                          {new Date(c.periodEnd).toLocaleDateString('es-CO')}
                        </td>
                        <td className="px-4 py-3 text-sm">{c.issuedBy}</td>
                        <td className="px-4 py-3 text-xs text-gray-500">
                          {new Date(c.issuedAt).toLocaleString('es-CO')}
                        </td>
                        <td className="px-4 py-3">
                          {c.pdfRef ? (
                            <button onClick={() => openPdf(`${API}/certificates/${c.folio}/pdf`)}
                              className="text-xs bg-gray-800 hover:bg-gray-700 px-3 py-1.5 rounded">
                              Abrir PDF
                            </button>
                          ) : <span className="text-xs text-gray-600">sin PDF</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            <div className="flex items-center gap-3 mb-4 flex-wrap">
              <span className="text-sm text-gray-400">Apruebas como <b className="text-gray-200">{user?.name}</b> ({user?.email}), queda en el registro de auditoría.</span>
              <button onClick={fetchReports}
                className="text-sm bg-gray-800 hover:bg-gray-700 px-4 py-1.5 rounded ml-auto">Actualizar</button>
            </div>

            <h3 className="text-lg font-semibold mb-3">
              Pendientes de aprobación
              {reports.length > 0 && (
                <span className="ml-2 text-xs bg-yellow-600 px-2 py-0.5 rounded-full align-middle">{reports.length}</span>
              )}
            </h3>

            {reports.length === 0 ? (
              <div className="bg-gray-900 border border-gray-800 rounded p-8 text-center text-gray-500">
                <p className="text-3xl mb-2">✓</p>
                <p className="text-gray-300">No hay reportes pendientes.</p>
                <p className="text-xs mt-2">
                  Los borradores se generan solos cuando una amenaza se confirma como HIGH o CRITICAL
                  y tiene evidencia preservada.
                </p>
              </div>
            ) : (
              <div className="space-y-2 mb-8">
                {reports.map(report => (
                  <div key={report.id}
                    className="bg-gray-900 border border-gray-800 rounded p-4 flex items-start justify-between gap-4 flex-wrap">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-xs bg-gray-800 px-2 py-0.5 rounded">{report.authority}</span>
                        <span className="text-xs text-gray-500">{report.country}</span>
                        <span className="font-mono text-sm text-red-400 break-all">{report.threat?.domain}</span>
                      </div>
                      <p className="text-sm text-gray-300 mt-1">{report.subject}</p>
                      <p className="text-[11px] text-gray-600 mt-1">
                        {report.threat?.company?.name} · redactado {timeAgo(report.createdAt)}
                      </p>
                    </div>
                    <div className="flex gap-2 shrink-0">
                      <button onClick={() => setOpenReport(report)}
                        className="text-xs bg-gray-800 hover:bg-gray-700 px-3 py-2 rounded">Leer y revisar</button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {authorities?.authorities && (
              <div className="bg-gray-900 border border-gray-800 rounded p-4">
                <p className="text-sm font-semibold text-gray-300 mb-3">Autoridades configuradas</p>
                <div className="grid md:grid-cols-2 gap-2">
                  {authorities.authorities.map((a: any) => (
                    <div key={a.id} className="bg-gray-950 border border-gray-800 rounded p-3">
                      <div className="flex items-start justify-between gap-2">
                        <span className="text-sm">{a.name}</span>
                        <span className="text-[10px] bg-gray-800 px-2 py-0.5 rounded shrink-0">
                          {a.channel === 'EMAIL' ? 'correo' : a.channel === 'WEB_FORM' ? 'formulario' : 'API'}
                        </span>
                      </div>
                      <p className="text-[11px] text-gray-500 mt-1">{a.country} · {a.scope}</p>
                      {a.notes && <p className="text-[11px] text-yellow-600/80 mt-1">{a.notes}</p>}
                      <p className="text-[10px] text-gray-700 mt-1">canal verificado {a.verifiedAt}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {openReport && (
          <Modal title={`Reporte a ${openReport.authority}`} onClose={() => setOpenReport(null)} wide>
            <p className="text-sm text-gray-400 mb-1">{openReport.subject}</p>
            <p className="text-xs text-gray-600 mb-4">
              Revisa el contenido antes de aprobar. Al aprobar se copia al portapapeles
              para que lo pegues en el canal de la autoridad.
            </p>
            <pre className="bg-gray-950 border border-gray-800 rounded p-4 text-xs text-gray-300 whitespace-pre-wrap max-h-[50vh] overflow-y-auto">
{openReport.body}
            </pre>
            <div className="flex gap-2 mt-4 justify-end">
              <button onClick={() => setOpenReport(null)}
                className="text-sm bg-gray-800 hover:bg-gray-700 px-4 py-2 rounded">Cerrar</button>
              <button onClick={() => approveAndCopy(openReport)}
                className="text-sm bg-red-600 hover:bg-red-700 px-4 py-2 rounded font-medium">
                Apruebo el envío
              </button>
            </div>
          </Modal>
        )}

        {activeTab === 'analyze' && (
          <div className="max-w-3xl">
            <h2 className="text-2xl font-bold mb-2">Verificar un dominio ahora</h2>
            <p className="text-gray-400 text-sm mb-6 leading-relaxed">
              VERAX vigila solo. Esto es para cuando alguien te reenvía un enlace sospechoso y
              necesitas un veredicto en el momento, sin esperar al próximo barrido. Usa exactamente
              el mismo motor que la detección automática: resuelve, captura con navegador y compara
              contra la línea base de la empresa.
            </p>

            <div className="bg-gray-900 p-6 rounded space-y-4 border border-gray-800">
              <div>
                <label className="text-sm text-gray-400 block mb-2">Dominio o enlace a verificar</label>
                <input type="text" value={domain} onChange={e => setDomain(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') analyzeDomain(); }}
                  placeholder="bancolombia-clave.com  ·  https://sitio.co/login"
                  className="w-full bg-gray-800 border border-gray-700 rounded px-4 py-2 text-white font-mono focus:outline-none focus:border-blue-500" />
                <p className="text-xs text-gray-500 mt-1">Acepta la URL completa; VERAX se queda con el dominio.</p>
              </div>
              <div>
                <label className="text-sm text-gray-400 block mb-2">¿A quién intenta suplantar?</label>
                <select value={companyId} onChange={e => setCompanyId(e.target.value)}
                  className="w-full bg-gray-800 border border-gray-700 rounded px-4 py-2 text-white focus:outline-none focus:border-blue-500">
                  <option value="">Selecciona la empresa vigilada</option>
                  {companies.map(c => <option key={c.id} value={c.id}>{c.name} ({c.domain})</option>)}
                </select>
                <p className="text-xs text-gray-500 mt-1">La comparación siempre es contra un original conocido.</p>
              </div>
              <button onClick={analyzeDomain} disabled={analyzing || !domain || !companyId}
                className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-gray-700 disabled:text-gray-500 py-3 rounded font-semibold transition-colors">
                {analyzing ? 'Verificando… (captura + comparación, ~15 s)' : 'Verificar ahora'}
              </button>
            </div>

            {result?.error && (
              <div className="mt-6 bg-red-950 border border-red-800 rounded p-4 text-red-300 text-sm">
                {result.error}
              </div>
            )}

            {result && !result.error && (
              <div className="mt-6 bg-gray-900 rounded border border-gray-800 overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-800 flex items-center justify-between flex-wrap gap-3">
                  <div>
                    <p className="font-mono text-lg">{result.unicode || result.domain}</p>
                    {result.unicode && result.unicode !== result.domain && (
                      <p className="text-xs text-gray-500 font-mono">punycode: {result.domain}</p>
                    )}
                  </div>
                  <span className={`px-3 py-1 rounded text-sm font-bold ${
                    result.veredicto === 'PROPIO' ? 'bg-blue-900 text-blue-300' :
                    result.severity === 'CRITICAL' ? 'bg-red-900 text-red-200' :
                    result.severity === 'HIGH' ? 'bg-orange-900 text-orange-200' :
                    result.severity === 'MEDIUM' ? 'bg-yellow-900 text-yellow-200' :
                    result.severity === 'LOW' ? 'bg-gray-700 text-gray-200' :
                    'bg-green-900 text-green-200'}`}>
                    {result.veredicto === 'PROPIO' ? 'Es un dominio propio' : result.severity}
                  </span>
                </div>

                {result.veredicto === 'PROPIO' ? (
                  <div className="px-6 py-5 text-sm text-gray-300">
                    Este dominio está en la lista de activos propios de la empresa. No se registra como amenaza.
                  </div>
                ) : (
                  <div className="px-6 py-5 space-y-5">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="bg-gray-800 p-4 rounded">
                        <p className="text-gray-400 text-xs mb-2">Score de amenaza</p>
                        <div className="flex items-baseline gap-2">
                          <span className="text-3xl font-bold">{result.threatScore}</span>
                          <span className="text-gray-500 text-sm">/100</span>
                        </div>
                        <div className="h-1.5 bg-gray-700 rounded mt-2 overflow-hidden">
                          <div className="h-full bg-blue-500" style={{ width: `${result.threatScore}%` }} />
                        </div>
                      </div>
                      <div className="bg-gray-800 p-4 rounded">
                        <p className="text-gray-400 text-xs mb-2">Score de suplantación activa</p>
                        <div className="flex items-baseline gap-2">
                          <span className="text-3xl font-bold">{result.impersonationScore}</span>
                          <span className="text-gray-500 text-sm">/100</span>
                        </div>
                        <div className="h-1.5 bg-gray-700 rounded mt-2 overflow-hidden">
                          <div className="h-full bg-orange-500" style={{ width: `${result.impersonationScore}%` }} />
                        </div>
                        <p className="text-xs text-gray-500 mt-2">
                          0 = no hay evidencia de que copie la marca ni de que capture datos.
                        </p>
                      </div>
                    </div>

                    <div>
                      <p className="text-gray-400 text-xs mb-2">Qué encontró VERAX</p>
                      {result.evidence?.length ? (
                        <ul className="space-y-1">
                          {result.evidence.map((e: string, i: number) => (
                            <li key={i} className="text-sm text-gray-200 flex gap-2">
                              <span className="text-gray-600">·</span><span>{e}</span>
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <p className="text-sm text-gray-400">Ninguna señal de suplantación. El sitio no imita a {companies.find(c => c.id === companyId)?.name || 'la empresa'}.</p>
                      )}
                    </div>

                    {result['señales'] && (
                      <div>
                        <p className="text-gray-400 text-xs mb-2">Señales del nombre de dominio</p>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
                          <div className="bg-gray-800 p-3 rounded">
                            <p className="text-gray-500 text-xs">Homoglifos</p>
                            <p className="font-bold">{result['señales'].homoglifos ? 'Sí' : 'No'}</p>
                          </div>
                          <div className="bg-gray-800 p-3 rounded">
                            <p className="text-gray-500 text-xs">TLD de alto abuso</p>
                            <p className="font-bold">{result['señales'].tldDeAltoAbuso ? 'Sí' : 'No'}</p>
                          </div>
                          <div className="bg-gray-800 p-3 rounded">
                            <p className="text-gray-500 text-xs">Distancia de edición</p>
                            <p className="font-bold">{result['señales'].distanciaDeEdicion}</p>
                          </div>
                          <div className="bg-gray-800 p-3 rounded">
                            <p className="text-gray-500 text-xs">Contiene la marca</p>
                            <p className="font-bold">{result['señales'].contieneLaMarca ? 'Sí' : 'No'}</p>
                          </div>
                        </div>
                      </div>
                    )}

                    {result.avisoBaseline && (
                      <div className="bg-yellow-950 border border-yellow-800 rounded p-3 text-sm text-yellow-200">
                        {result.avisoBaseline}
                      </div>
                    )}

                    {result.comparadoCon && (
                      <p className="text-xs text-gray-500">Comparado contra la línea base de {result.comparadoCon}.</p>
                    )}

                    {result.threatId && (
                      <button onClick={() => setActiveTab('threats')}
                        className="text-blue-400 hover:text-blue-300 text-sm font-semibold">
                        Quedó registrada en Amenazas →
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {activeTab === 'threats' && (
          <div>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold">Amenazas</h2>
              <button onClick={fetchData} className="bg-gray-700 hover:bg-gray-600 px-4 py-2 rounded text-sm">Actualizar</button>
            </div>
            <div className="space-y-2">
              {threats.map(threat => (
                <div key={threat.id} onClick={() => setSelectedThreat(threat)}
                  className="bg-gray-900 p-4 rounded border border-gray-800 hover:border-gray-600 cursor-pointer transition-colors">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 flex-wrap">
                        <p className="font-mono text-red-400 font-bold">{threat.domain}</p>
                        <ScoreBadge score={threat.score} />
                        {threat.metadata?.impersonationScore > 0 && (
                          <span className="text-xs bg-orange-900 text-orange-400 px-2 py-1 rounded font-bold">
                            Suplantación {threat.metadata.impersonationScore}%
                          </span>
                        )}
                        {threat.metadata?.contentAnalysis?.hasPaymentForm && (
                          <span className="text-xs bg-red-900 text-red-400 px-2 py-1 rounded">Pasarela de pago</span>
                        )}
                        {threat.metadata?.contentAnalysis?.hasLoginForm && (
                          <span className="text-xs bg-yellow-900 text-yellow-400 px-2 py-1 rounded">Form login</span>
                        )}
                        {threat.validationDecision === 'CONFIRMED' && (
                          <span className="text-xs bg-red-900 text-red-200 px-2 py-1 rounded font-bold">✔ Confirmado por VERAX</span>
                        )}
                        {threat.validationDecision === 'NEEDS_REVIEW' && (
                          <span className="text-xs bg-amber-900 text-amber-300 px-2 py-1 rounded">Para revisión</span>
                        )}
                        {threat.emailSent && <span className="text-xs text-green-400">Alertado</span>}
                      </div>
                      <div className="flex gap-4 mt-1 text-sm text-gray-400">
                        <span>{threat.company?.name}</span>
                        <span>{threat.metadata?.variantType || threat.type}</span>
                        <span>{new Date(threat.detectedAt).toLocaleString()}</span>
                      </div>
                    </div>
                    <span className="text-gray-500 text-sm ml-4">Ver detalle →</span>
                  </div>
                </div>
              ))}
              {threats.length === 0 && (
                <div className="text-center py-12 text-gray-500">
                  <p>No hay amenazas detectadas</p>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'admin' && (
          <div>
            <UsersPanel companies={companies.map(c => ({ id: c.id, name: c.name }))} />
            <AlertsPanel />
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold">Empresas vigiladas</h2>
              <button onClick={openCreateCompany}
                className="bg-blue-600 hover:bg-blue-700 px-4 py-2 rounded font-semibold transition-colors">
                + Nueva Empresa
              </button>
            </div>
            <div className="space-y-4">
              {companies.map(company => (
                <div key={company.id} className="bg-gray-900 border border-gray-800 rounded-lg p-5">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-3 flex-wrap">
                        <h3 className="text-lg font-bold">{company.name}</h3>
                        <span className={`text-xs px-2 py-1 rounded font-bold ${company.active ? 'bg-green-900 text-green-400' : 'bg-gray-700 text-gray-400'}`}>
                          {company.active ? 'ACTIVA' : 'INACTIVA'}
                        </span>
                        <span className={`text-xs px-2 py-1 rounded font-bold ${company.scanEnabled ? 'bg-blue-900 text-blue-400' : 'bg-gray-700 text-gray-500'}`}>
                          {company.scanEnabled ? `Scan cada ${company.scanFrequency}h` : 'Scan desactivado'}
                        </span>
                        {company.threats?.length > 0 && (
                          <span className="text-xs px-2 py-1 rounded bg-red-900 text-red-400 font-bold">
                            {company.threats.length} amenazas
                          </span>
                        )}
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-sm text-gray-400">
                        <span><span className="font-mono text-blue-400">{company.domain}</span></span>
                        <span>{company.industry || '-'}</span>
                        <span>Contacto: {company.contactEmail || '-'}</span>
                        <span>Admin: {company.adminEmail || '-'}</span>
                        {company.alertEmails?.length > 0 && (
                          <span className="col-span-2">Alertas: {company.alertEmails.join(', ')}</span>
                        )}
                        <span className="col-span-2">Último scan: {company.lastScannedAt ? new Date(company.lastScannedAt).toLocaleString() : 'Nunca'}</span>
                      </div>
                    </div>
                    <div className="flex flex-col gap-2 ml-4">
                      <button onClick={() => startProactiveScan(company.id, company.name)}
                        disabled={scanning === company.id}
                        className="bg-green-700 hover:bg-green-600 disabled:bg-gray-700 px-3 py-2 rounded text-sm font-semibold transition-colors">
                        {scanning === company.id ? `${scanState ?? 'Escaneando…'}` : 'Scan Ahora'}
                      </button>
                      <button onClick={() => viewCompanyScans(company)}
                        className="bg-blue-900 hover:bg-blue-800 px-3 py-2 rounded text-sm transition-colors">
                        Historial
                      </button>
                      <button onClick={() => toggleScanEnabled(company)}
                        className={`px-3 py-2 rounded text-sm transition-colors ${company.scanEnabled ? 'bg-yellow-900 hover:bg-yellow-800 text-yellow-400' : 'bg-gray-700 hover:bg-gray-600 text-gray-400'}`}>
                        {company.scanEnabled ? 'Pausar' : '▶️ Activar'}
                      </button>
                      <button onClick={() => openEditCompany(company)}
                        className="bg-gray-700 hover:bg-gray-600 px-3 py-2 rounded text-sm transition-colors">
                        Editar
                      </button>
                      <button onClick={() => setDeleteConfirm(company.id)}
                        className="bg-red-900 hover:bg-red-800 px-3 py-2 rounded text-sm transition-colors">
                        Eliminar
                      </button>
                    </div>
                  </div>
                </div>
              ))}
              {companies.length === 0 && (
                <div className="text-center py-12 text-gray-500">
                  <p>No hay empresas registradas</p>
                  <button onClick={openCreateCompany} className="mt-3 text-blue-400 hover:text-blue-300 text-sm">
                    + Crear primera empresa
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </main>

      {oficialForm && (
        <Modal title="Registrar cuenta oficial" onClose={() => setOficialForm(null)}>
          <div className="space-y-4">
            <p className="text-sm text-gray-400">
              Las cuentas oficiales son la línea base: lo que VERAX sabe que es auténtico y contra lo
              que compara todo lo demás. Sin ellas, cualquier cuenta parecida parece sospechosa.
            </p>
            <div>
              <label className="text-sm text-gray-400 block mb-1">Plataforma</label>
              <select value={oficialForm.platform}
                onChange={e => setOficialForm({ ...oficialForm, platform: e.target.value })}
                className="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-white">
                {['X', 'LINKEDIN', 'FACEBOOK', 'INSTAGRAM', 'TIKTOK', 'YOUTUBE', 'THREADS'].map(pl => (
                  <option key={pl} value={pl}>{pl}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-sm text-gray-400 block mb-1">Handle o URL del perfil</label>
              <input type="text" value={oficialForm.handle}
                onChange={e => setOficialForm({ ...oficialForm, handle: e.target.value })}
                placeholder="@mimarca  ·  https://x.com/mimarca"
                className="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-white font-mono text-sm" />
            </div>
            <div className="flex gap-2">
              <button onClick={guardarOficial} disabled={!oficialForm.handle.trim()}
                className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-700 py-2.5 rounded font-semibold">
                Registrar como oficial
              </button>
              <button onClick={() => setOficialForm(null)}
                className="px-4 bg-gray-800 hover:bg-gray-700 rounded text-sm">Cancelar</button>
            </div>
          </div>
        </Modal>
      )}

      {execForm && (
        <Modal title={execForm.id ? 'Editar directivo' : 'Nuevo directivo vigilado'}
          onClose={() => setExecForm(null)} wide>
          <div className="space-y-4">
            <p className="text-sm text-gray-400">
              VERAX buscará perfiles que suplanten a esta persona. Es el vector del fraude del CEO:
              una cuenta que se hace pasar por un directivo pidiendo una transferencia urgente.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="text-sm text-gray-400 block mb-1">Nombre completo *</label>
                <input type="text" value={execForm.name}
                  onChange={e => setExecForm({ ...execForm, name: e.target.value })}
                  placeholder="Como aparece en sus perfiles públicos"
                  className="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-white" />
              </div>
              <div>
                <label className="text-sm text-gray-400 block mb-1">Empresa *</label>
                <select value={execForm.companyId}
                  onChange={e => setExecForm({ ...execForm, companyId: e.target.value })}
                  className="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-white">
                  {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div>
                <label className="text-sm text-gray-400 block mb-1">Cargo</label>
                <input type="text" value={execForm.position}
                  onChange={e => setExecForm({ ...execForm, position: e.target.value })}
                  placeholder="Gerente General"
                  className="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-white" />
              </div>
              <div>
                <label className="text-sm text-gray-400 block mb-1">Correo</label>
                <input type="email" value={execForm.email}
                  onChange={e => setExecForm({ ...execForm, email: e.target.value })}
                  className="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-white" />
              </div>
            </div>

            <div>
              <label className="text-sm text-gray-400 block mb-1">Otros nombres con los que aparece</label>
              <input type="text" value={execForm.aliasesStr}
                onChange={e => setExecForm({ ...execForm, aliasesStr: e.target.value })}
                placeholder="Separados por comas: Elena Restrepo, E. Restrepo Gómez"
                className="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-white" />
              <p className="text-xs text-gray-500 mt-1">
                Un suplantador rara vez copia el nombre exacto. Cuantas más variantes conozca VERAX,
                más difícil es que una se le escape.
              </p>
            </div>

            <div>
              <label className="text-sm text-gray-400 block mb-1">Perfiles legítimos conocidos</label>
              <textarea value={execForm.profilesStr} rows={3}
                onChange={e => setExecForm({ ...execForm, profilesStr: e.target.value })}
                placeholder={'https://www.linkedin.com/in/...\nhttps://x.com/...'}
                className="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-white font-mono text-sm" />
              <p className="text-xs text-gray-500 mt-1">
                Uno por línea. Son la línea base: lo que VERAX sabe que es auténtico, y contra lo que
                compara cualquier perfil que encuentre después. Sin esto, todo perfil parece sospechoso.
              </p>
            </div>

            <div>
              <label className="text-sm text-gray-400 block mb-1">Foto de referencia <span className="text-gray-600">(URL)</span></label>
              <input type="text" value={execForm.photoRef}
                onChange={e => setExecForm({ ...execForm, photoRef: e.target.value })}
                placeholder="https://…/foto.jpg"
                className="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-white font-mono text-sm" />
              <p className="text-xs text-gray-500 mt-1">
                La foto pesa más que el nombre en la detección: un impostor puede inventarse el nombre,
                pero casi siempre roba la foto real.
              </p>
            </div>

            {/* Ley 1581: sin autorización no arranca la vigilancia */}
            <div className="bg-yellow-950/20 border border-yellow-900/50 rounded p-3">
              <label className="text-sm text-yellow-200 block mb-1">
                Autorización de tratamiento de datos personales
              </label>
              <input type="text" value={execForm.consentRef}
                onChange={e => setExecForm({ ...execForm, consentRef: e.target.value })}
                placeholder="Referencia del documento: AUT-2026-0142"
                className="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-white" />
              <p className="text-xs text-yellow-200/70 mt-2">
                Vigilar a una persona es tratamiento de datos personales. La Ley 1581 de 2012 exige
                base legal documentada. <strong>Sin esta referencia el directivo queda registrado pero
                la vigilancia no arranca</strong>, y el sistema lo dirá en la tabla.
              </p>
            </div>

            <div className="flex gap-2 pt-1">
              <button onClick={guardarDirectivo}
                disabled={savingExec || !execForm.name.trim() || !execForm.companyId}
                className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-700 disabled:text-gray-500 py-2.5 rounded font-semibold">
                {savingExec ? 'Guardando…' : execForm.id ? 'Guardar cambios' : 'Registrar directivo'}
              </button>
              <button onClick={() => setExecForm(null)}
                className="px-4 bg-gray-800 hover:bg-gray-700 rounded text-sm">Cancelar</button>
            </div>
          </div>
        </Modal>
      )}

      {certForm && (
        <Modal title="Emitir constancia de vigilancia" onClose={() => setCertForm(null)}>
          <div className="space-y-4">
            <p className="text-sm text-gray-400">
              El documento congela las cifras del periodo y lleva folio y huella SHA-256.
              Quien lo reciba puede comprobar que es auténtico sin necesitar acceso al sistema.
            </p>
            {certForm.aviso && (
              <p className="text-xs bg-gray-800 border border-gray-700 rounded px-3 py-2 text-gray-300">
                {certForm.aviso}
              </p>
            )}
            <p className="text-xs text-gray-500">
              Solo se puede acreditar un periodo en el que hubo barridos. Un periodo sin vigilancia
              no produce una constancia limpia: produce un documento que afirma que no se encontró
              nada cuando la verdad es que no se miró.
            </p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm text-gray-400 block mb-1">Desde</label>
                <input type="date" value={certForm.desde}
                  onChange={e => setCertForm({ ...certForm, desde: e.target.value })}
                  className="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-white" />
              </div>
              <div>
                <label className="text-sm text-gray-400 block mb-1">Hasta</label>
                <input type="date" value={certForm.hasta}
                  onChange={e => setCertForm({ ...certForm, hasta: e.target.value })}
                  className="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-white" />
              </div>
            </div>
            <div>
              <label className="text-sm text-gray-400 block mb-1">Solicitada por</label>
              <input type="text" value={certForm.solicitadaPor}
                onChange={e => setCertForm({ ...certForm, solicitadaPor: e.target.value })}
                placeholder="Tu nombre y cargo"
                className="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-white" />
              <p className="text-xs text-gray-500 mt-1">
                Queda impreso en la constancia: emitirla es un acto con responsable.
              </p>
            </div>
            <div>
              <label className="text-sm text-gray-400 block mb-1">Destinatario <span className="text-gray-600">(opcional)</span></label>
              <input type="text" value={certForm.destinatario}
                onChange={e => setCertForm({ ...certForm, destinatario: e.target.value })}
                placeholder="A quién va dirigida"
                className="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-white" />
            </div>
            <div className="flex gap-2 pt-1">
              <button onClick={emitirConstancia} disabled={emitiendo}
                className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-700 py-2.5 rounded font-semibold">
                {emitiendo ? 'Emitiendo…' : 'Emitir constancia'}
              </button>
              <button onClick={() => setCertForm(null)}
                className="px-4 bg-gray-800 hover:bg-gray-700 rounded text-sm">Cancelar</button>
            </div>
          </div>
        </Modal>
      )}

      {selectedThreat && (
        <Modal title="Detalle de la amenaza" onClose={() => setSelectedThreat(null)} wide>
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <span className="font-mono text-red-400 text-xl font-bold">{selectedThreat.domain}</span>
              <ScoreBadge score={selectedThreat.score} />
            </div>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div className="bg-gray-800 p-3 rounded">
                <p className="text-gray-400 text-xs mb-1">Empresa afectada</p>
                <p className="font-bold">{selectedThreat.company?.name}</p>
              </div>
              <div className="bg-gray-800 p-3 rounded">
                <p className="text-gray-400 text-xs mb-1">Tipo de variante</p>
                <p className="font-bold">{selectedThreat.metadata?.variantType || selectedThreat.type}</p>
              </div>
              <div className="bg-gray-800 p-3 rounded">
                <p className="text-gray-400 text-xs mb-1">Similitud visual con el original</p>
                <p className="font-bold text-red-400">{selectedThreat.metadata?.visualSimilarityPercent ?? 0}%</p>
              </div>
              <div className="bg-gray-800 p-3 rounded">
                <p className="text-gray-400 text-xs mb-1">Score suplantación</p>
                <p className="font-bold text-orange-400">{selectedThreat.metadata?.impersonationScore || 0}/100</p>
              </div>
              <div className="bg-gray-800 p-3 rounded">
                <p className="text-gray-400 text-xs mb-1">Tiene sitio web</p>
                <p className="font-bold">{selectedThreat.metadata?.hasWeb ? 'Sí' : 'No'}</p>
              </div>
              <div className="bg-gray-800 p-3 rounded">
                <p className="text-gray-400 text-xs mb-1">Certificado SSL</p>
                <p className="font-bold">{selectedThreat.metadata?.hasCertificate ? 'Sí' : 'No'}</p>
              </div>
            </div>
            {selectedThreat.validationCriteria && (
              <div className="bg-gray-800 border border-gray-700 rounded p-4 text-sm">
                <p className="font-bold mb-2 text-gray-200">
                  Validación automática: {({ CONFIRMED: 'confirmado', NEEDS_REVIEW: 'para revisión', WATCH: 'en vigilancia', INACTIVE: 'fuera de línea', DISMISSED: 'descartado' } as Record<string, string>)[selectedThreat.validationDecision ?? ''] ?? selectedThreat.validationDecision}
                  <span className="text-xs text-gray-500 font-normal"> · {selectedThreat.validatedBy} · {selectedThreat.validatedAt ? new Date(selectedThreat.validatedAt).toLocaleString('es-CO') : ''}</span>
                </p>
                {['identity', 'harm'].map(k => (selectedThreat.validationCriteria[k] ?? []).length > 0 && (
                  <div key={k} className="mb-1">
                    <p className="text-xs text-gray-400">{k === 'identity' ? 'Se presenta como la marca' : 'Mecanismo de fraude'}</p>
                    <ul className="list-disc ml-5 text-gray-300">{selectedThreat.validationCriteria[k].map((c: string) => <li key={c}>{c}</li>)}</ul>
                  </div>
                ))}
                {selectedThreat.validationCriteria.note && <p className="text-xs text-gray-400 mt-1">{selectedThreat.validationCriteria.note}</p>}
                <p className="text-[11px] text-gray-500 mt-2">Confirmar no envía nada: la solicitud de baja y el reporte salen solo cuando un ADMIN los aprueba.</p>
              </div>
            )}
            {selectedThreat.metadata?.impersonationDetails?.length > 0 && (
              <div className="bg-red-950 border border-red-800 rounded p-4">
                <p className="text-red-400 font-bold mb-2">Indicadores de Suplantación:</p>
                <ul className="space-y-1">
                  {selectedThreat.metadata.impersonationDetails.map((detail: string, i: number) => (
                    <li key={i} className="text-sm text-red-300">{detail}</li>
                  ))}
                </ul>
              </div>
            )}
            {selectedThreat.metadata?.contentAnalysis && (
              <div className="bg-gray-800 rounded p-4 text-sm">
                <p className="font-bold mb-2 text-gray-300">Análisis de Contenido:</p>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <span className={selectedThreat.metadata.contentAnalysis.hasLoginForm ? 'text-red-400' : 'text-gray-500'}>
                    {selectedThreat.metadata.contentAnalysis.hasLoginForm ? '✗' : '✓'} Formulario de login
                  </span>
                  <span className={selectedThreat.metadata.contentAnalysis.hasPaymentForm ? 'text-red-400' : 'text-gray-500'}>
                    {selectedThreat.metadata.contentAnalysis.hasPaymentForm ? '✗' : '✓'} Pasarela de pago
                  </span>
                  <span className={selectedThreat.metadata.contentAnalysis.hasBrandName ? 'text-red-400' : 'text-gray-500'}>
                    {selectedThreat.metadata.contentAnalysis.hasBrandName ? '✗' : '✓'} Marca suplantada
                  </span>
                  <span className={selectedThreat.metadata.contentAnalysis.hasCopiedContent ? 'text-red-400' : 'text-gray-500'}>
                    {selectedThreat.metadata.contentAnalysis.hasCopiedContent ? '✗' : '✓'} Contenido copiado
                  </span>
                </div>
                {selectedThreat.metadata.contentAnalysis.paymentKeywords?.length > 0 && (
                  <p className="mt-2 text-orange-400 text-xs">
                    Keywords de pago: {selectedThreat.metadata.contentAnalysis.paymentKeywords.join(', ')}
                  </p>
                )}
              </div>
            )}
            <div className="text-xs text-gray-500">
              Detectado: {new Date(selectedThreat.detectedAt).toLocaleString()} | Email alertado: {selectedThreat.emailSent ? 'Sí' : 'No'}
            </div>
          </div>
        </Modal>
      )}

      {selectedCompanyScans && (
        <Modal title={`Historial: ${selectedCompanyScans.company.name}`} onClose={() => setSelectedCompanyScans(null)} wide>
          <div className="space-y-3">
            {selectedCompanyScans.scans.length === 0 && (
              <p className="text-gray-500 text-center py-8">No hay scans registrados</p>
            )}
            {selectedCompanyScans.scans.map(scan => (
              <div key={scan.id} className="bg-gray-800 p-4 rounded">
                <div className="flex items-center justify-between">
                  <div>
                    <span className={`text-xs font-bold px-2 py-1 rounded mr-2 ${
                      scan.status === 'COMPLETED' ? 'bg-green-900 text-green-400' :
                      scan.status === 'RUNNING' ? 'bg-blue-900 text-blue-400' :
                      scan.status === 'FAILED' ? 'bg-red-900 text-red-400' : 'bg-gray-700 text-gray-400'
                    }`}>{scan.status}</span>
                    <span className="text-sm">{new Date(scan.startedAt).toLocaleString()}</span>
                  </div>
                  <div className="text-right text-sm">
                    <span className="text-gray-400">{scan.totalVariants} variantes</span>
                    <span className={`ml-3 font-bold ${scan.activeThreats > 0 ? 'text-red-400' : 'text-green-400'}`}>
                      {scan.activeThreats} amenazas
                    </span>
                  </div>
                </div>
                {scan.completedAt && (
                  <p className="text-xs text-gray-500 mt-1">Completado: {new Date(scan.completedAt).toLocaleString()}</p>
                )}
              </div>
            ))}
          </div>
        </Modal>
      )}

      {showCompanyModal && (
        <Modal title={editingCompany ? 'Editar empresa' : 'Nueva empresa'} onClose={() => setShowCompanyModal(false)}>
          <div className="space-y-3">
            <div>
              <label className="text-sm text-gray-400 block mb-1">Nombre *</label>
              <input type="text" value={companyForm.name}
                onChange={e => setCompanyForm({ ...companyForm, name: e.target.value })}
                placeholder="Ej: Bancolombia"
                className="w-full bg-gray-800 border border-gray-700 rounded px-4 py-2 text-white focus:outline-none focus:border-blue-500" />
            </div>
            <div>
              <label className="text-sm text-gray-400 block mb-1">Dominio principal *</label>
              <input type="text" value={companyForm.domain}
                onChange={e => setCompanyForm({ ...companyForm, domain: e.target.value })}
                placeholder="Ej: bancolombia.com"
                className="w-full bg-gray-800 border border-gray-700 rounded px-4 py-2 text-white focus:outline-none focus:border-blue-500" />
            </div>
            <div>
              <label className="text-sm text-gray-400 block mb-1">Industria</label>
              <select value={companyForm.industry}
                onChange={e => setCompanyForm({ ...companyForm, industry: e.target.value })}
                className="w-full bg-gray-800 border border-gray-700 rounded px-4 py-2 text-white focus:outline-none focus:border-blue-500">
                <option value="">Seleccionar</option>
                <option value="Banking">Banca y Finanzas</option>
                <option value="Technology">Tecnología</option>
                <option value="Healthcare">Salud</option>
                <option value="Retail">Retail</option>
                <option value="Government">Gobierno</option>
                <option value="Education">Educación</option>
                <option value="Energy">Energía</option>
                <option value="Other">Otro</option>
              </select>
            </div>
            <div>
              <label className="text-sm text-gray-400 block mb-1">Correo de contacto (si no hay responsable, recibe las alertas)</label>
              <input type="email" value={companyForm.contactEmail}
                onChange={e => setCompanyForm({ ...companyForm, contactEmail: e.target.value })}
                placeholder="seguridad@empresa.com"
                className="w-full bg-gray-800 border border-gray-700 rounded px-4 py-2 text-white focus:outline-none focus:border-blue-500" />
            </div>
            <div>
              <label className="text-sm text-gray-400 block mb-1">Responsable de la empresa (recibe las alertas)</label>
              <input type="email" value={companyForm.adminEmail}
                onChange={e => setCompanyForm({ ...companyForm, adminEmail: e.target.value })}
                placeholder="riesgos@empresa.com"
                className="w-full bg-gray-800 border border-gray-700 rounded px-4 py-2 text-white focus:outline-none focus:border-blue-500" />
            </div>
            <div>
              <label className="text-sm text-gray-400 block mb-1">Correos adicionales de alerta</label>
              <input type="text" value={companyForm.alertEmailsStr}
                onChange={e => setCompanyForm({ ...companyForm, alertEmailsStr: e.target.value })}
                placeholder="ceo@empresa.com, ciso@empresa.com"
                className="w-full bg-gray-800 border border-gray-700 rounded px-4 py-2 text-white focus:outline-none focus:border-blue-500" />
              <p className="text-xs text-gray-500 mt-1">Separados por coma</p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm text-gray-400 block mb-1">Frecuencia de scan</label>
                <select value={companyForm.scanFrequency}
                  onChange={e => setCompanyForm({ ...companyForm, scanFrequency: parseInt(e.target.value) })}
                  className="w-full bg-gray-800 border border-gray-700 rounded px-4 py-2 text-white focus:outline-none focus:border-blue-500">
                  <option value={6}>Cada 6 horas</option>
                  <option value={12}>Cada 12 horas</option>
                  <option value={24}>Cada 24 horas</option>
                  <option value={48}>Cada 48 horas</option>
                  <option value={168}>Semanal</option>
                </select>
              </div>
              <div>
                <label className="text-sm text-gray-400 block mb-1">Scan automático</label>
                <div className="flex items-center mt-3">
                  <button type="button"
                    onClick={() => setCompanyForm({ ...companyForm, scanEnabled: !companyForm.scanEnabled })}
                    className={`w-12 h-6 rounded-full transition-colors relative ${companyForm.scanEnabled ? 'bg-blue-600' : 'bg-gray-600'}`}>
                    <div className={`absolute top-0.5 w-5 h-5 bg-white rounded-full transition-all ${companyForm.scanEnabled ? 'left-6' : 'left-0.5'}`} />
                  </button>
                  <span className="ml-2 text-sm text-gray-400">{companyForm.scanEnabled ? 'Activo' : 'Inactivo'}</span>
                </div>
              </div>
            </div>
            <div className="flex gap-3 pt-2">
              <button onClick={() => setShowCompanyModal(false)}
                className="flex-1 bg-gray-700 hover:bg-gray-600 py-2 rounded font-semibold transition-colors">
                Cancelar
              </button>
              <button onClick={saveCompany} disabled={saving || !companyForm.name || !companyForm.domain}
                className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-700 py-2 rounded font-semibold transition-colors">
                {saving ? 'Guardando...' : editingCompany ? 'Actualizar' : 'Crear Empresa'}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {deleteConfirm && (
        <Modal title="Confirmar eliminación" onClose={() => setDeleteConfirm(null)}>
          <p className="text-gray-400 mb-6">¿Estás seguro que deseas eliminar esta empresa?</p>
          <div className="flex gap-3">
            <button onClick={() => setDeleteConfirm(null)}
              className="flex-1 bg-gray-700 hover:bg-gray-600 py-2 rounded font-semibold">Cancelar</button>
            <button onClick={() => deleteCompany(deleteConfirm)}
              className="flex-1 bg-red-600 hover:bg-red-700 py-2 rounded font-semibold">Eliminar</button>
          </div>
        </Modal>
      )}
    </div>
  );
}
